import React, { useState } from 'react';
import { formatBs, formatCurrency } from '../utils/format';
import { PAYMENT_METHODS, PAYMENT_TOLERANCE_USD, paymentMethodCurrency, paymentMethodIcon } from '../utils/payments';

const round2 = (n) => Math.round(n * 100) / 100;

function MethodGroup({ title, currency, onAdd, disabled }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--orig-text-muted)', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 6 }}>
        {title}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {PAYMENT_METHODS.filter(m => m.currency === currency).map(m => (
          <button
            key={m.value}
            type="button"
            className="btn-orig btn-orig-outline"
            onClick={() => onAdd(m.value)}
            disabled={disabled}
            style={{ padding: '7px 12px', fontSize: 12.5 }}
          >
            <i className={`bi ${m.icon}`} />
            {m.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CobroModal({ totalUsd, exchangeRate, client, saving, error, onCancel, onConfirm }) {
  const [payments, setPayments] = useState([]);

  const rate = Number(exchangeRate) || 0;
  const totalBs = round2(totalUsd * rate);

  // Lo pagado se lleva a Bs para compararlo con el total en Bs
  const paidBsEq = (list) => list.reduce((sum, p) => {
    const amount = Number(p.amount) || 0;
    return sum + (paymentMethodCurrency(p.method) === 'USD' ? amount * rate : amount);
  }, 0);

  const remainingBs = round2(totalBs - paidBsEq(payments));
  const remainingUsd = rate ? remainingBs / rate : 0;
  const settled = Math.abs(remainingUsd) <= PAYMENT_TOLERANCE_USD;
  const isComplete = settled && payments.length > 0 && payments.every(p => Number(p.amount) > 0);

  // Monto que falta, expresado en la moneda del método
  function remainingIn(method, list) {
    const rest = round2(totalBs - paidBsEq(list));
    if (rest <= 0) return '';
    return paymentMethodCurrency(method) === 'USD' ? round2(rest / rate).toFixed(2) : rest.toFixed(2);
  }

  function addMethod(method) {
    setPayments(prev => [...prev, { method, amount: remainingIn(method, prev), reference: '' }]);
  }

  function updatePayment(index, field, value) {
    setPayments(prev => prev.map((p, i) => {
      if (i !== index) return p;
      const next = { ...p, [field]: value };
      // Al cambiar de moneda, recalcular el monto con lo que falta
      if (field === 'method' && paymentMethodCurrency(value) !== paymentMethodCurrency(p.method)) {
        next.amount = remainingIn(value, prev.filter((_, j) => j !== index));
      }
      return next;
    }));
  }

  function fillRemaining(index) {
    setPayments(prev => prev.map((p, i) => (
      i === index ? { ...p, amount: remainingIn(p.method, prev.filter((_, j) => j !== index)) } : p
    )));
  }

  function removePayment(index) {
    setPayments(prev => prev.filter((_, i) => i !== index));
  }

  function handleConfirm() {
    if (!isComplete) return;
    onConfirm(payments.map(p => ({
      method: p.method,
      amount: round2(Number(p.amount)),
      reference: p.reference.trim() || null,
    })));
  }

  const statusColor = settled ? 'green' : remainingBs > 0 ? 'amber' : 'red';

  return (
    <div
      onClick={saving ? undefined : onCancel}
      style={{
        position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="section-card"
        style={{ width: '100%', maxWidth: 600, marginBottom: 0, maxHeight: '90vh', overflowY: 'auto' }}
      >
        <div className="section-header">
          <h6>
            <i className="bi bi-wallet2" style={{ color: 'var(--orig-green)' }} />
            Cobrar Factura
          </h6>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            style={{ background: 'none', border: 'none', color: 'var(--orig-text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}
            title="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="section-body">
          {error && (
            <div className="alert-orig alert-error" style={{ marginBottom: 16 }}>
              <i className="bi bi-exclamation-triangle-fill" />
              {error}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>Cliente</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--orig-text)' }}>{client.name}</div>
              <div style={{ fontSize: 11.5, color: 'var(--orig-text-muted)' }}>{client.nationality}-{client.document}</div>
              <div style={{ fontSize: 11.5, color: 'var(--orig-text-muted)', marginTop: 4 }}>
                Tasa: <strong>{formatBs(rate)}</strong> / $
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: 'var(--orig-text-muted)' }}>Total a pagar</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--orig-green-bright)', fontFamily: 'var(--orig-font-display)', lineHeight: 1.1 }}>
                {formatCurrency(totalUsd)}
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--orig-text)' }}>{formatBs(totalBs)}</div>
            </div>
          </div>

          <MethodGroup title="Dólares ($)" currency="USD" onAdd={addMethod} disabled={saving} />
          <MethodGroup title="Bolívares (Bs)" currency="BS" onAdd={addMethod} disabled={saving} />

          {payments.length === 0 ? (
            <div style={{
              padding: '18px 12px', textAlign: 'center', fontSize: 12.5, color: 'var(--orig-text-muted)',
              border: '1px dashed var(--orig-border)', borderRadius: 'var(--orig-radius)', margin: '8px 0 16px',
            }}>
              Toque una forma de pago. Puede combinar varias (ej: $20 por Zelle + el resto por punto).
            </div>
          ) : (
            <div style={{ margin: '8px 0 16px' }}>
              {payments.map((p, idx) => {
                const isUsd = paymentMethodCurrency(p.method) === 'USD';
                const amount = Number(p.amount) || 0;
                return (
                  <div
                    key={idx}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                      padding: '8px 0', borderBottom: '1px solid var(--orig-border)',
                    }}
                  >
                    <i className={`bi ${paymentMethodIcon(p.method)}`} style={{ fontSize: 18, color: 'var(--orig-green)', width: 22 }} />
                    <select
                      className="form-select"
                      value={p.method}
                      onChange={e => updatePayment(idx, 'method', e.target.value)}
                      style={{ width: 150, marginBottom: 0 }}
                    >
                      {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                    <div style={{ position: 'relative' }}>
                      <span style={{
                        position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                        fontSize: 12, fontWeight: 700, color: 'var(--orig-text-muted)', pointerEvents: 'none',
                      }}>
                        {isUsd ? '$' : 'Bs'}
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        className="form-control"
                        placeholder="Monto"
                        value={p.amount}
                        onChange={e => updatePayment(idx, 'amount', e.target.value)}
                        style={{ width: 130, paddingLeft: isUsd ? 22 : 30, marginBottom: 0 }}
                      />
                    </div>
                    {!p.method.startsWith('EFECTIVO') && (
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Referencia"
                        value={p.reference}
                        onChange={e => updatePayment(idx, 'reference', e.target.value)}
                        style={{ flex: 1, minWidth: 100, marginBottom: 0 }}
                      />
                    )}
                    <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                      {!settled && (
                        <button
                          type="button"
                          className="btn-orig btn-orig-outline"
                          onClick={() => fillRemaining(idx)}
                          style={{ padding: '5px 8px', fontSize: 11 }}
                          title="Completar con el monto restante"
                        >
                          <i className="bi bi-magic" />
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-orig btn-orig-outline"
                        onClick={() => removePayment(idx)}
                        style={{ padding: '5px 10px', color: 'var(--orig-red-light)' }}
                        title="Quitar pago"
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                    {amount > 0 && (
                      <div style={{ width: '100%', paddingLeft: 30, fontSize: 11, color: 'var(--orig-text-muted)' }}>
                        ≈ {isUsd ? formatBs(amount * rate) : formatCurrency(amount / rate)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{
            padding: '10px 14px', borderRadius: 'var(--orig-radius)',
            background: `var(--orig-${statusColor}-dim)`, border: '1px solid var(--orig-border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--orig-text-muted)' }}>
              {settled ? 'Pago completo' : remainingBs > 0 ? 'Restante' : 'Excedente'}
            </span>
            {!settled && (
              <div style={{ textAlign: 'right', color: `var(--orig-${statusColor}-light)` }}>
                <div style={{ fontSize: 16, fontWeight: 800 }}>{formatBs(Math.abs(remainingBs))}</div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{formatCurrency(Math.abs(remainingUsd))}</div>
              </div>
            )}
            {settled && <i className="bi bi-check-circle-fill" style={{ fontSize: 20, color: 'var(--orig-green-bright)' }} />}
          </div>
          {!settled && remainingBs < 0 && (
            <div style={{ fontSize: 12, color: 'var(--orig-red-light)', marginTop: 8 }}>
              Los pagos superan el total. Registre solo lo cobrado (sin el vuelto).
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 10,
          padding: '14px 20px', borderTop: '1px solid var(--orig-border)',
        }}>
          <button type="button" className="btn-orig btn-orig-outline" onClick={onCancel} disabled={saving}>
            Volver
          </button>
          <button
            type="button"
            className="btn-orig btn-orig-green"
            onClick={handleConfirm}
            disabled={saving || !isComplete}
          >
            <i className="bi bi-check2-circle" />
            {saving ? 'Facturando...' : 'Confirmar y Facturar'}
          </button>
        </div>
      </div>
    </div>
  );
}
